import { Filetype, type Stat, type Vfs } from "idfweb-vfs";

import { devNode, devReaddir, devStat, inDev } from "./devices.ts";
import { Errno, HostFailure, NotImplemented, ProcExit, WasiError } from "./errno.ts";
import {
    HTTP_FAILED,
    blockUntil,
    exitStatus,
    httpRequested,
    httpStatus,
    nextPid,
    processExited,
    processStarting,
} from "./shared.ts";
import {
    FIRST_PREOPEN_FD,
    FdTable,
    Fdflags,
    Fdflagsext,
    Fstflags,
    Lookupflags,
    Oflags,
    OpenFile,
    Pipe,
    Rights,
    Whence,
    openOptions,
    resolveInPreopens,
    type FdEntry,
    type FdSnapshot,
    type Preopen,
} from "./wasi-fs.ts";

/** WASIX host implementation providing system call imports to WebAssembly modules. */

export type { Preopen };
export { FIRST_PREOPEN_FD };

/** Request payload for spawning a process via proc_spawn3. */
export interface SpawnRequest {
    /** The executable, already resolved through PATH. */
    guestPath: string;
    args: string[];
    env: Record<string, string>;
    cwd: string;
    /** The child's descriptor table, and the references it holds. */
    fds: FdSnapshot;
    pid: number;
    parentPid: number;
}

/** Request payload for host.http_download network fetches. */
export interface HttpRequest {
    url: string;
    /** Absolute guest path, already checked to be inside a preopen. */
    path: string;
    pid: number;
}

export interface HostOptions {
    vfs: Vfs;
    args: string[];
    env: Record<string, string>;
    cwd: string;
    /** Ignored when `fds` is given: a child inherits its parent's table. */
    preopens: Preopen[];
    /** The descriptor table to start with, for a process that was spawned. */
    fds?: FdTable | undefined;
    onStdout(bytes: Uint8Array): void;
    onStderr(bytes: Uint8Array): void;
    trace?: ((line: string) => void) | undefined;
    spawn?: ((req: SpawnRequest) => void) | undefined;
    http?: ((req: HttpRequest) => void) | undefined;
    pid?: number | undefined;
    parentPid?: number | undefined;
}

/** How long `host.http_download` waits for an answer before giving up. */
export const HTTP_TIMEOUT_MS = 10 * 60 * 1000;

type SyscallArg = number | bigint;
type Syscall = (...args: never[]) => number;

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const EMPTY = new Uint8Array(0);

/** sizeof(__wasi_proc_spawn_fd_op_t), from the sysroot's api_wasix.h. */
const FD_OP_SIZE = 56;
/** sizeof(__wasi_dirent_t), which each name follows unpadded. */
const DIRENT_SIZE = 24;
/** sizeof(__wasi_subscription_t) and sizeof(__wasi_event_t). */
const SUBSCRIPTION_SIZE = 48;
const EVENT_SIZE = 32;

const FdOp = { CLOSE: 0, DUP2: 1, OPEN: 2, CHDIR: 3, FCHDIR: 4 } as const;
const Eventtype = { CLOCK: 0, FD_READ: 1, FD_WRITE: 2 } as const;
const JoinStatusType = { NOTHING: 0, EXIT_NORMAL: 1 } as const;
const OptionTag = { NONE: 0, SOME: 1 } as const;
/** __WASI_SUBSCRIPTION_CLOCK_ABSTIME, on a clock subscription's flags. */
const ABSTIME = 1;
/** proc_join's flags: return rather than wait if no child has finished. */
const JOIN_NON_BLOCKING = 1;
/** WASI signal numbers whose default action is to do nothing: none, CHLD, CONT, STOP to TTOU, URG, WINCH. */
const IGNORED_SIGNALS = new Set([0, 16, 17, 18, 19, 20, 21, 22, 27]);
const SIGPIPE = 13;
const DISPOSITION_DEFAULT = 0;

/** What a process owns apart from its memory: saved by vfork, restored when the child leaves. */
interface ProcessContext {
    pid: number;
    parentPid: number;
    fds: FdTable;
    cwd: string;
    children: number[];
}

export class WasixHost {
    memory!: WebAssembly.Memory;
    /** The instance's exports, for the signal callback. */
    exports: WebAssembly.Exports | undefined;

    readonly #opts: HostOptions;
    readonly #vfs: Vfs;
    readonly #argv: Uint8Array[];
    readonly #environ: Uint8Array[];
    #fds: FdTable;
    /** Children spawned and not yet collected by proc_join. */
    #children: number[] = [];
    #cwd: string;
    #pid: number;
    #parentPid: number;
    /**
     * The parents of a vfork child running in this instance, innermost last.
     * The child shares the parent's memory and stack until it execs or exits.
     */
    readonly #vforked: ProcessContext[] = [];
    /** The export registered with callback_signal, through which libc runs handlers and default actions. */
    #signalCallback: string | undefined;
    #view: DataView | undefined;
    #viewBuffer: ArrayBufferLike | undefined;

    constructor(opts: HostOptions) {
        this.#opts = opts;
        this.#vfs = opts.vfs;
        this.#argv = opts.args.map((a) => encoder.encode(a + "\0"));
        this.#environ = Object.entries(opts.env).map(([k, v]) => encoder.encode(`${k}=${v}\0`));
        this.#fds = opts.fds ?? new FdTable(opts.vfs, opts.preopens);
        this.#cwd = opts.cwd;
        this.#pid = opts.pid ?? 1;
        this.#parentPid = opts.parentPid ?? 0;
    }

    /** Release host file descriptors once the run is over. */
    dispose(): void {
        this.#fds.closeAll();
        this.#vfs.processEnded();
    }

    // A child inherits these: output that is not redirected onto a pipe goes
    // wherever its parent's went, which for the outermost tool is the CLI.
    get onStdout(): (bytes: Uint8Array) => void {
        return this.#opts.onStdout;
    }

    get onStderr(): (bytes: Uint8Array) => void {
        return this.#opts.onStderr;
    }

    get trace(): ((line: string) => void) | undefined {
        return this.#opts.trace;
    }

    // ---- memory access -----------------------------------------------------

    get view(): DataView {
        if (this.#view === undefined || this.#viewBuffer !== this.memory.buffer) {
            this.#viewBuffer = this.memory.buffer;
            this.#view = new DataView(this.memory.buffer);
        }
        return this.#view;
    }

    bytes(ptr: number, len: number): Uint8Array {
        return new Uint8Array(this.memory.buffer, ptr, len);
    }

    writeBytes(ptr: number, src: Uint8Array): void {
        this.bytes(ptr, src.length).set(src);
    }

    /** Read an `__wasi_ciovec_t` / `__wasi_iovec_t` array. */
    iovecs(ptr: number, count: number): Uint8Array[] {
        const view = this.view;
        const out: Uint8Array[] = [];
        for (let i = 0; i < count; i++) {
            const base = view.getUint32(ptr + i * 8, true);
            const len = view.getUint32(ptr + i * 8 + 4, true);
            out.push(this.bytes(base, len));
        }
        return out;
    }

    #string(ptr: number, len: number): string {
        // Copied because TextDecoder refuses a view of a SharedArrayBuffer.
        return decoder.decode(this.bytes(ptr, len).slice());
    }

    // ---- import object -----------------------------------------------------

    // ---- import object -----------------------------------------------------

    /** Build the WebAssembly import object based on declared module imports. */
    imports(declared: readonly WebAssembly.ModuleImportDescriptor[]): WebAssembly.Imports {
        const out: WebAssembly.Imports = {};
        for (const { module, name, kind } of declared) {
            const ns = (out[module] ??= {});
            if (kind === "memory") {
                ns[name] = this.memory;
                continue;
            }
            if (kind !== "function") {
                throw new Error(`cannot supply ${kind} import ${module}.${name}`);
            }
            const impl = (this.#impls as Record<string, Syscall | undefined>)[name];
            ns[name] = impl === undefined ? this.#stub(module, name) : this.#wrap(module, name, impl);
        }
        return out;
    }

    /** The first host failure encountered during execution. */
    failure: HostFailure | undefined;

    record(failure: HostFailure): void {
        this.failure ??= failure;
    }

    /** Return an import stub for an unimplemented syscall that records the failure and throws. */
    #stub(ns: string, name: string): () => never {
        const failure = new NotImplemented(`${ns}.${name}`);
        return () => {
            this.record(failure);
            throw failure;
        };
    }

    /** Wrap a syscall implementation to translate WasiError exceptions to return codes. */
    #wrap(ns: string, name: string, impl: Syscall) {
        return (...args: SyscallArg[]): number => {
            let errno: number;
            try {
                errno = (impl as (...a: SyscallArg[]) => number)(...args);
            } catch (e) {
                if (e instanceof HostFailure) this.record(e);
                if (!(e instanceof WasiError)) throw e;
                errno = e.errno;
            }
            this.#opts.trace?.(`${ns}.${name}(${args.join(", ")}) -> ${errno}`);
            return errno;
        };
    }

    // ---- implemented syscalls ---------------------------------------------

    readonly #impls = {
        args_sizes_get: (countPtr: number, bufSizePtr: number): number => this.#sizes(this.#argv, countPtr, bufSizePtr),
        args_get: (pointersPtr: number, bufPtr: number): number => this.#strings(this.#argv, pointersPtr, bufPtr),

        environ_sizes_get: (countPtr: number, bufSizePtr: number): number =>
            this.#sizes(this.#environ, countPtr, bufSizePtr),
        environ_get: (pointersPtr: number, bufPtr: number): number => this.#strings(this.#environ, pointersPtr, bufPtr),

        random_get: (bufPtr: number, bufLen: number): number => {
            randomBytes(this.bytes(bufPtr, bufLen));
            return Errno.SUCCESS;
        },

        clock_time_get: (clockId: number, _precision: bigint, retPtr: number): number => {
            // 0 realtime, 1 monotonic, both from performance.now(), the clock
            // poll_oneoff deadlines use. Realtime needs its sub-millisecond steps:
            // libc's mkstemp derives names from it, and GCC drivers started in the
            // same millisecond would otherwise collide on their temporary files.
            const ns =
                clockId === 0
                    ? BigInt(Math.round((performance.timeOrigin + performance.now()) * 1e6))
                    : clockId === 1
                      ? BigInt(Math.round(performance.now() * 1e6))
                      : undefined;
            if (ns === undefined) throw new WasiError(Errno.INVAL);
            this.view.setBigUint64(retPtr, ns, true);
            return Errno.SUCCESS;
        },

        sched_yield: (): number => Errno.SUCCESS,

        /** Report single-threaded parallelism. */
        thread_parallelism: (retPtr: number): number => {
            this.view.setUint32(retPtr, 1, true);
            return Errno.SUCCESS;
        },

        /** Return -1 (EAGAIN) to signal thread creation is unavailable without terminating probes. */
        "thread-spawn": (_startArg: number): number => -1,

        /** In a vfork child, returns to the parent; libc then jumps back to the vfork call. */
        proc_exit2: (code: number): number => {
            if (this.#vforked.length === 0) throw new ProcExit(code);
            const child = this.#pid;
            this.#fds.closeAll();
            this.#leaveVfork();
            processExited(child, code);
            return Errno.SUCCESS;
        },

        // A process has one thread, so a signal to a thread is one to itself.
        thread_signal: (_tid: number, signal: number): number => {
            this.#raise(signal);
            return Errno.SUCCESS;
        },
        proc_raise: (signal: number): number => {
            this.#raise(signal);
            return Errno.SUCCESS;
        },

        // libc applies these at startup through its own sigaction, which is
        // also what sets up its default actions: without one, the callback
        // traps on a signal the program has no handler for.
        proc_signals_sizes_get: (retPtr: number): number => {
            this.view.setUint32(retPtr, 1, true);
            return Errno.SUCCESS;
        },
        proc_signals_get: (bufPtr: number): number => {
            this.view.setUint8(bufPtr, SIGPIPE);
            this.view.setUint8(bufPtr + 1, DISPOSITION_DEFAULT);
            return Errno.SUCCESS;
        },
        callback_signal: (namePtr: number, nameLen: number): number => {
            this.#signalCallback = this.#string(namePtr, nameLen);
            return Errno.SUCCESS;
        },

        /** Report non-terminal TTY characteristics to disable ANSI escape sequences. */
        tty_get: (retPtr: number): number => {
            const view = this.view;
            view.setUint32(retPtr, 80, true); // cols
            view.setUint32(retPtr + 4, 25, true); // rows
            view.setUint32(retPtr + 8, 0, true); // width, pixels
            view.setUint32(retPtr + 12, 0, true); // height, pixels
            for (let i = 16; i < 24; i++) view.setUint8(retPtr + i, 0);
            return Errno.SUCCESS;
        },

        /** Return current working directory, writing a null terminator if capacity allows. */
        getcwd: (pathPtr: number, pathLenPtr: number): number => {
            const cwd = encoder.encode(this.#cwd);
            const view = this.view;
            const capacity = view.getUint32(pathLenPtr, true);
            view.setUint32(pathLenPtr, cwd.length, true);
            if (cwd.length > capacity) throw new WasiError(Errno.RANGE);
            if (pathPtr === 0 || capacity === 0) throw new WasiError(Errno.INVAL);
            this.writeBytes(pathPtr, cwd);
            if (cwd.length < capacity) view.setUint8(pathPtr + cwd.length, 0);
            return Errno.SUCCESS;
        },

        chdir: (pathPtr: number, pathLen: number): number => {
            const path = this.#string(pathPtr, pathLen);
            const guestPath = resolveInPreopens(
                this.#fds.preopens,
                path.startsWith("/") ? path : `${this.#cwd}/${path}`,
                Errno.NOENT,
            );
            if (this.#stat(guestPath, true).filetype !== Filetype.DIRECTORY) {
                throw new WasiError(Errno.NOTDIR);
            }
            this.#cwd = guestPath;
            return Errno.SUCCESS;
        },

        proc_id: (retPtr: number): number => {
            this.view.setUint32(retPtr, this.#pid, true);
            return Errno.SUCCESS;
        },

        proc_parent: (pid: number, retPtr: number): number => {
            if (pid !== this.#pid) throw new WasiError(Errno.CHILD);
            this.view.setUint32(retPtr, this.#parentPid, true);
            return Errno.SUCCESS;
        },

        // ---- processes -----------------------------------------------------

        /** Spawn a child process with posix_spawn file actions and inherited descriptors. */
        proc_spawn3: (
            namePtr: number,
            nameLen: number,
            argsPtr: number,
            argsLen: number,
            envsPtr: number,
            envsLen: number,
            fdOpsPtr: number,
            fdOpsLen: number,
            _signalsPtr: number,
            _signalsLen: number,
            searchPath: number,
            pathPtr: number,
            pathLen: number,
            retPtr: number,
        ): number => {
            const spawn = this.#opts.spawn;
            if (spawn === undefined) throw new NotImplemented("wasix_32v1.proc_spawn3");

            const name = this.#string(namePtr, nameLen);
            const args = this.#stringArray(argsPtr, argsLen);
            const env = this.#childEnv(envsPtr, envsLen);
            const searchIn = pathPtr === 0 ? undefined : this.#string(pathPtr, pathLen);
            const target = this.#findExecutable(name, searchPath !== 0, searchIn);

            const fds = this.#fds.fork();
            let cwd = this.#cwd;
            for (let i = 0; i < fdOpsLen; i++) {
                cwd = this.#applyFdOp(fds, fdOpsPtr + i * FD_OP_SIZE, cwd);
            }

            const pid = nextPid();
            // Before the spawn is even asked for, so that a parent which goes
            // straight into proc_join finds a child that counts as live.
            processStarting(pid);
            const request = { guestPath: target, args, env, cwd, pid, parentPid: this.#pid };
            this.#launch(spawn, request, fds);

            this.#children.push(pid);
            this.view.setUint32(retPtr, pid, true);
            return Errno.SUCCESS;
        },

        /**
         * Start a vfork child: from here until it execs or exits, this instance
         * runs as the child, with a copy of the descriptor table and a new pid.
         */
        proc_fork_env: (retPtr: number): number => {
            if (this.#opts.spawn === undefined) {
                throw new NotImplemented("wasix_32v1.proc_fork_env");
            }
            const pid = nextPid();
            processStarting(pid);
            this.#children.push(pid);
            this.#vforked.push({
                pid: this.#pid,
                parentPid: this.#parentPid,
                fds: this.#fds,
                cwd: this.#cwd,
                children: this.#children,
            });
            this.#fds = this.#fds.fork();
            this.#children = [];
            this.#parentPid = this.#pid;
            this.#pid = pid;
            this.view.setUint32(retPtr, pid, true);
            return Errno.SUCCESS;
        },

        /**
         * Run a program as the vfork child, which then continues in its own
         * worker, and return to the parent. Replacing a process that was not
         * vforked is not supported.
         */
        proc_exec4: (
            namePtr: number,
            nameLen: number,
            argsPtr: number,
            argsLen: number,
            envsPtr: number,
            envsLen: number,
            searchPath: number,
            pathPtr: number,
            pathLen: number,
        ): number => {
            const spawn = this.#opts.spawn;
            if (spawn === undefined || this.#vforked.length === 0) {
                throw new NotImplemented("wasix_32v1.proc_exec4 outside vfork");
            }
            const name = this.#string(namePtr, nameLen);
            const searchIn = pathPtr === 0 ? undefined : this.#string(pathPtr, pathLen);
            const target = this.#findExecutable(name, searchPath !== 0, searchIn);
            const request = {
                guestPath: target,
                args: this.#stringArray(argsPtr, argsLen),
                env: this.#childEnv(envsPtr, envsLen),
                cwd: this.#cwd,
                pid: this.#pid,
                parentPid: this.#parentPid,
            };
            const fds = this.#fds;
            this.#leaveVfork();
            this.#launch(spawn, request, fds);
            return Errno.SUCCESS;
        },

        /**
         * Host import for guest network access: fetches a URL into a file in the guest
         * filesystem. Returns the HTTP status code or HTTP_FAILED on failure/timeout.
         */
        http_download: (urlPtr: number, urlLen: number, pathPtr: number, pathLen: number): number => {
            const http = this.#opts.http;
            if (http === undefined) throw new NotImplemented("host.http_download");

            const url = this.#string(urlPtr, urlLen);
            const pid = this.#pid;
            let path: string;
            try {
                path = this.#onFilesystem(resolveInPreopens(this.#fds.preopens, this.#string(pathPtr, pathLen)));
            } catch (e) {
                if (!(e instanceof WasiError)) throw e;
                this.#opts.trace?.(`http_download: ${url}: path refused, errno ${e.errno}`);
                return HTTP_FAILED;
            }

            httpRequested(pid);
            http({ url, path, pid });
            const status = blockUntil(() => httpStatus(pid), `downloading ${url}`, {
                deadline: performance.now() + HTTP_TIMEOUT_MS,
            });
            return status ?? HTTP_FAILED;
        },

        /** Waits for child process exit and retrieves its exit code. */
        proc_join: (pidPtr: number, flags: number, statusPtr: number): number => {
            let wanted: number | undefined;
            if (pidPtr !== 0 && this.view.getUint8(pidPtr) === OptionTag.SOME) {
                wanted = this.view.getUint32(pidPtr + 4, true);
            }
            const waiting = wanted === undefined ? this.#children : [wanted];
            if (waiting.length === 0 || (wanted !== undefined && !this.#children.includes(wanted))) {
                this.view.setUint8(statusPtr, JoinStatusType.NOTHING);
                return Errno.CHILD;
            }

            const finished = (): { pid: number; exitCode: number } | undefined => {
                for (const pid of waiting) {
                    const exitCode = exitStatus(pid);
                    if (exitCode !== undefined) return { pid, exitCode };
                }
                return undefined;
            };
            const child =
                (flags & JOIN_NON_BLOCKING) !== 0
                    ? finished()
                    : blockUntil(finished, `waiting for ${describe(waiting)} to exit`, { waiter: this.#pid });
            if (child === undefined) {
                this.view.setUint8(statusPtr, JoinStatusType.NOTHING);
                return Errno.SUCCESS;
            }
            this.#children.splice(this.#children.indexOf(child.pid), 1);

            if (pidPtr !== 0) {
                this.view.setUint8(pidPtr, OptionTag.SOME);
                this.view.setUint32(pidPtr + 4, child.pid, true);
            }
            this.view.setUint8(statusPtr, JoinStatusType.EXIT_NORMAL);
            this.view.setUint16(statusPtr + 2, child.exitCode & 0xffff, true);
            return Errno.SUCCESS;
        },

        // ---- descriptors ---------------------------------------------------

        fd_pipe: (readPtr: number, writePtr: number): number => {
            const pipe = Pipe.create();
            const read = this.#fds.insert({ kind: "pipe", pipe, end: "read", fdflags: 0 });
            const write = this.#fds.insert({ kind: "pipe", pipe, end: "write", fdflags: 0 });
            const view = this.view;
            view.setUint32(readPtr, read, true);
            view.setUint32(writePtr, write, true);
            return Errno.SUCCESS;
        },

        /** Duplicates fd to the lowest free descriptor at or above minFd (F_DUPFD semantics). */
        fd_dup2: (fd: number, minFd: number, cloexec: number, retPtr: number): number => {
            this.view.setUint32(retPtr, this.#fds.dup(fd, minFd, cloexec !== 0), true);
            return Errno.SUCCESS;
        },

        /** wasix-libc's dup2: `from` stays open, and `to` is not close-on-exec. */
        fd_renumber: (from: number, to: number): number => {
            if (from === to) {
                this.#fds.get(from);
                return Errno.SUCCESS;
            }
            this.#fds.renumber(this.#fds.dup(from), to);
            this.#fds.setCloexec(to, false);
            return Errno.SUCCESS;
        },

        fd_fdflags_get: (fd: number, retPtr: number): number => {
            this.view.setUint16(retPtr, this.#fds.cloexec(fd) ? Fdflagsext.CLOEXEC : 0, true);
            return Errno.SUCCESS;
        },

        fd_fdflags_set: (fd: number, flags: number): number => {
            this.#fds.setCloexec(fd, (flags & Fdflagsext.CLOEXEC) !== 0);
            return Errno.SUCCESS;
        },

        fd_fdstat_set_flags: (fd: number, fdflags: number): number => {
            const entry = this.#fds.get(fd);
            // Non-blocking mode is currently only handled by pipe descriptors.
            if (entry.kind === "pipe") entry.fdflags = fdflags;
            else if (entry.kind === "file") entry.fdflags = fdflags;
            return Errno.SUCCESS;
        },

        /** Polls file descriptor events or waits until the specified clock deadline. */
        poll_oneoff: (inPtr: number, outPtr: number, count: number, retPtr: number): number => {
            const view = this.view;
            const subscriptions: { userdata: bigint; type: number; fd: number }[] = [];
            let clock: { userdata: bigint; deadline: number } | undefined;

            for (let i = 0; i < count; i++) {
                const base = inPtr + i * SUBSCRIPTION_SIZE;
                const userdata = view.getBigUint64(base, true);
                const type = view.getUint8(base + 8);
                if (type === Eventtype.CLOCK) {
                    const deadline = this.#deadline(base);
                    if (clock === undefined || deadline < clock.deadline) {
                        clock = { userdata, deadline };
                    }
                    continue;
                }
                subscriptions.push({ userdata, type, fd: view.getUint32(base + 16, true) });
            }

            const ready = (): number | undefined => {
                let emitted = 0;
                for (const { userdata, type, fd } of subscriptions) {
                    const state = this.#readiness(fd, type);
                    if (state === undefined) continue;
                    const out = outPtr + emitted++ * EVENT_SIZE;
                    view.setBigUint64(out, userdata, true);
                    view.setUint16(out + 8, state.error, true);
                    view.setUint8(out + 10, type);
                    view.setBigUint64(out + 16, BigInt(state.nbytes), true);
                    view.setUint16(out + 24, state.hangup ? 1 : 0, true); // FD_READWRITE_HANGUP
                }
                return emitted === 0 ? undefined : emitted;
            };

            const what = `polling ${subscriptions.length} descriptors`;
            let emitted = blockUntil(ready, what, { waiter: this.#pid, deadline: clock?.deadline });
            if (emitted === undefined) {
                // Timeout elapsed with no descriptor events ready.
                const out = outPtr;
                view.setBigUint64(out, clock!.userdata, true);
                view.setUint16(out + 8, Errno.SUCCESS, true);
                view.setUint8(out + 10, Eventtype.CLOCK);
                emitted = 1;
            }

            view.setUint32(retPtr, emitted, true);
            return Errno.SUCCESS;
        },

        fd_prestat_get: (fd: number, retPtr: number): number => {
            const view = this.view;
            view.setUint8(retPtr, 0); // __WASI_PREOPENTYPE_DIR
            view.setUint32(retPtr + 4, encoder.encode(this.preopen(fd)).length, true);
            return Errno.SUCCESS;
        },

        fd_prestat_dir_name: (fd: number, pathPtr: number, pathLen: number): number => {
            const name = encoder.encode(this.preopen(fd));
            if (name.length > pathLen) throw new WasiError(Errno.NAMETOOLONG);
            this.writeBytes(pathPtr, name);
            return Errno.SUCCESS;
        },

        fd_close: (fd: number): number => {
            this.#fds.close(fd);
            return Errno.SUCCESS;
        },

        fd_dup: (fd: number, retPtr: number): number => {
            this.view.setUint32(retPtr, this.#fds.dup(fd), true);
            return Errno.SUCCESS;
        },

        fd_fdstat_get: (fd: number, retPtr: number): number => {
            const entry = this.#fds.get(fd);
            const view = this.view;
            view.setUint8(retPtr, filetypeOfEntry(entry));
            view.setUint16(retPtr + 2, entry.kind === "file" || entry.kind === "pipe" ? entry.fdflags : 0, true);
            view.setBigUint64(retPtr + 8, Rights.ALL, true);
            view.setBigUint64(retPtr + 16, Rights.ALL, true);
            return Errno.SUCCESS;
        },

        fd_filestat_get: (fd: number, retPtr: number): number => {
            const entry = this.#fds.get(fd);
            if (entry.kind === "stdio" || entry.kind === "pipe" || entry.kind === "device") {
                return this.#writeEmptyFilestat(retPtr, filetypeOfEntry(entry));
            }
            const stats =
                entry.kind === "file" ? this.#vfs.statHandle(entry.file.handle) : this.#stat(entry.guestPath, true);
            this.#writeFilestat(retPtr, stats);
            return Errno.SUCCESS;
        },

        fd_filestat_set_size: (fd: number, size: bigint): number => {
            const file = this.#fds.file(fd);
            this.#vfs.truncate(file.handle, Number(size));
            return Errno.SUCCESS;
        },

        fd_seek: (fd: number, offset: bigint, whence: number, retPtr: number): number => {
            const entry = this.#fds.get(fd);
            if (entry.kind === "device") {
                // As on Linux: a device has no offset, and any seek lands at 0.
                this.view.setBigUint64(retPtr, 0n, true);
                return Errno.SUCCESS;
            }
            if (entry.kind !== "file") throw new WasiError(Errno.SPIPE);
            const file = entry.file;
            const base =
                whence === Whence.SET
                    ? 0
                    : whence === Whence.CUR
                      ? file.position
                      : whence === Whence.END
                        ? this.#vfs.statHandle(file.handle).size
                        : undefined;
            if (base === undefined) throw new WasiError(Errno.INVAL);
            const position = base + Number(offset);
            if (position < 0) throw new WasiError(Errno.INVAL);
            file.position = position;
            this.view.setBigUint64(retPtr, BigInt(position), true);
            return Errno.SUCCESS;
        },

        fd_tell: (fd: number, retPtr: number): number => this.#impls.fd_seek(fd, 0n, Whence.CUR, retPtr),

        fd_read: (fd: number, iovsPtr: number, iovsLen: number, readPtr: number): number =>
            this.#read(fd, iovsPtr, iovsLen, undefined, readPtr),
        fd_pread: (fd: number, iovsPtr: number, iovsLen: number, offset: bigint, readPtr: number): number =>
            this.#read(fd, iovsPtr, iovsLen, Number(offset), readPtr),

        fd_write: (fd: number, iovsPtr: number, iovsLen: number, writtenPtr: number): number =>
            this.#write(fd, iovsPtr, iovsLen, undefined, writtenPtr),
        fd_pwrite: (fd: number, iovsPtr: number, iovsLen: number, offset: bigint, writtenPtr: number): number =>
            this.#write(fd, iovsPtr, iovsLen, Number(offset), writtenPtr),

        /**
         * Reads directory entries into a buffer of packed __wasi_dirent_t + name records.
         * The cookie parameter specifies the entry index to resume from.
         */
        fd_readdir: (fd: number, bufPtr: number, bufLen: number, cookie: bigint, retPtr: number): number => {
            const entry = this.#fds.get(fd);
            if (entry.kind !== "dir") throw new WasiError(Errno.NOTDIR);
            const entries = this.#dirents(entry.guestPath);

            let offset = 0;
            for (let i = Number(cookie); i < entries.length && offset < bufLen; i++) {
                const { name, ino, filetype } = entries[i]!;
                const encoded = encoder.encode(name);
                const record = new Uint8Array(DIRENT_SIZE + encoded.length);
                const header = new DataView(record.buffer);
                header.setBigUint64(0, BigInt(i + 1), true); // cookie to resume after this
                header.setBigUint64(8, ino, true);
                header.setUint32(16, encoded.length, true);
                header.setUint8(20, filetype);
                record.set(encoded, DIRENT_SIZE);

                // Truncate if remaining buffer space is insufficient.
                const n = Math.min(record.length, bufLen - offset);
                this.writeBytes(bufPtr + offset, record.subarray(0, n));
                offset += n;
            }

            this.view.setUint32(retPtr, offset, true);
            return Errno.SUCCESS;
        },

        // ---- paths ---------------------------------------------------------

        path_open2: (
            dirfd: number,
            _dirflags: number,
            pathPtr: number,
            pathLen: number,
            oflags: number,
            rightsBase: bigint,
            _rightsInheriting: bigint,
            fdflags: number,
            _fdflagsext: number,
            retPtr: number,
        ): number => {
            // Resolve relative path against dirfd.
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen), outsideOpen(oflags));
            const fd = this.#open(this.#fds, "/", guestPath, oflags, rightsBase, fdflags);
            this.view.setUint32(retPtr, fd, true);
            return Errno.SUCCESS;
        },

        path_filestat_get: (dirfd: number, flags: number, pathPtr: number, pathLen: number, retPtr: number): number => {
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen), Errno.NOENT);
            const follow = (flags & Lookupflags.SYMLINK_FOLLOW) !== 0;
            this.#writeFilestat(retPtr, this.#stat(guestPath, follow));
            return Errno.SUCCESS;
        },

        /** Sets access and modification times for a path. */
        path_filestat_set_times: (
            dirfd: number,
            flags: number,
            pathPtr: number,
            pathLen: number,
            atim: bigint,
            mtim: bigint,
            fstflags: number,
        ): number => {
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen));
            const follow = (flags & Lookupflags.SYMLINK_FOLLOW) !== 0;
            if (inDev(guestPath)) {
                devNode(guestPath); // ENOENT for a name not in /dev
                return Errno.SUCCESS;
            }
            const [atime, mtime] = times(this.#vfs.stat(guestPath, follow), atim, mtim, fstflags);
            this.#vfs.setTimes(guestPath, atime, mtime, follow);
            return Errno.SUCCESS;
        },

        fd_filestat_set_times: (fd: number, atim: bigint, mtim: bigint, fstflags: number): number => {
            const file = this.#fds.file(fd);
            const stats = this.#vfs.statHandle(file.handle);
            const [atime, mtime] = times(stats, atim, mtim, fstflags);
            this.#vfs.setTimesHandle(file.handle, atime, mtime);
            return Errno.SUCCESS;
        },

        path_readlink: (
            dirfd: number,
            pathPtr: number,
            pathLen: number,
            bufPtr: number,
            bufLen: number,
            retPtr: number,
        ): number => {
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen), Errno.NOENT);
            if (inDev(guestPath)) {
                devNode(guestPath); // ENOENT for a name not in /dev
                throw new WasiError(Errno.INVAL);
            }
            const target = encoder.encode(this.#vfs.readlink(guestPath));
            const n = Math.min(target.length, bufLen);
            this.writeBytes(bufPtr, target.subarray(0, n));
            this.view.setUint32(retPtr, n, true);
            return Errno.SUCCESS;
        },

        path_rename: (
            oldDirfd: number,
            oldPathPtr: number,
            oldPathLen: number,
            newDirfd: number,
            newPathPtr: number,
            newPathLen: number,
        ): number => {
            const from = this.#fds.resolve(oldDirfd, this.#string(oldPathPtr, oldPathLen));
            const to = this.#fds.resolve(newDirfd, this.#string(newPathPtr, newPathLen));
            this.#vfs.rename(this.#onFilesystem(from), this.#onFilesystem(to));
            return Errno.SUCCESS;
        },

        path_unlink_file: (dirfd: number, pathPtr: number, pathLen: number): number => {
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen));
            this.#vfs.unlink(this.#onFilesystem(guestPath));
            return Errno.SUCCESS;
        },

        path_create_directory: (dirfd: number, pathPtr: number, pathLen: number): number => {
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen));
            this.#vfs.mkdir(this.#onFilesystem(guestPath));
            return Errno.SUCCESS;
        },

        path_remove_directory: (dirfd: number, pathPtr: number, pathLen: number): number => {
            const guestPath = this.#fds.resolve(dirfd, this.#string(pathPtr, pathLen));
            this.#vfs.rmdir(this.#onFilesystem(guestPath));
            return Errno.SUCCESS;
        },
    };

    // ---- helpers -----------------------------------------------------------

    /**
     * The preopened directory a descriptor refers to, for the two prestat calls.
     *
     * Read out of the descriptor table rather than indexed out of the preopen
     * list, because a spawned child's table is its parent's and a descriptor
     * number in it need not be the preopen that started there.
     */
    preopen(fd: number): Preopen {
        const entry = this.#fds.get(fd);
        if (entry.kind !== "dir" || !entry.preopen) throw new WasiError(Errno.BADF);
        return entry.guestPath;
    }

    /** A directory's entries in the order fd_readdir hands them out. */
    #dirents(guestPath: string): { name: string; ino: bigint; filetype: number }[] {
        const listed = inDev(guestPath) ? devReaddir() : this.#vfs.readdir(guestPath);
        const entries = listed.map((e) => ({
            name: e.name,
            ino: BigInt(e.ino),
            filetype: e.filetype as number,
        }));
        entries.push({ name: ".", ino: 0n, filetype: Filetype.DIRECTORY });
        entries.push({ name: "..", ino: 0n, filetype: Filetype.DIRECTORY });
        entries.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
        return entries;
    }

    /** Hand a child its descriptor table, whose references go with the snapshot. */
    #launch(spawn: (req: SpawnRequest) => void, request: Omit<SpawnRequest, "fds">, fds: FdTable): void {
        fds.closeCloexec();
        spawn({ ...request, fds: fds.snapshot() });
    }

    /** The environment a child gets: the one passed, or this process's own. */
    #childEnv(envsPtr: number, envsLen: number): Record<string, string> {
        if (envsPtr === 0) return { ...this.#opts.env };
        return parseEnv(this.#stringArray(envsPtr, envsLen));
    }

    /** Become the parent of the vfork child again. */
    #leaveVfork(): void {
        const parent = this.#vforked.pop()!;
        this.#pid = parent.pid;
        this.#parentPid = parent.parentPid;
        this.#fds = parent.fds;
        this.#cwd = parent.cwd;
        this.#children = parent.children;
    }

    /** Read a `char **` of `count` NUL-terminated strings. */
    #stringArray(ptr: number, count: number): string[] {
        const view = this.view;
        const out: string[] = [];
        for (let i = 0; i < count; i++) {
            const start = view.getUint32(ptr + i * 4, true);
            let end = start;
            while (view.getUint8(end) !== 0) end++;
            out.push(this.#string(start, end - start));
        }
        return out;
    }

    /**
     * Resolves the executable path for proc_spawn3. If searchPath is true and name
     * contains no slashes, looks up name in the provided PATH.
     */
    #findExecutable(name: string, searchPath: boolean, path: string | undefined): string {
        const candidates =
            !searchPath || name.includes("/")
                ? [name.startsWith("/") ? name : `${this.#cwd}/${name}`]
                : (path ?? "/usr/local/bin:/bin:/usr/bin")
                      .split(":")
                      .filter((dir) => dir !== "")
                      .map((dir) => `${dir}/${name}`);

        for (const candidate of candidates) {
            try {
                const guestPath = resolveInPreopens(this.#fds.preopens, candidate, Errno.NOENT);
                if (this.#vfs.stat(guestPath, true).filetype === Filetype.REGULAR_FILE) {
                    return guestPath;
                }
            } catch {
                // Not found or inaccessible under this preopen path.
            }
        }
        // ENOENT, as execvp reports it: a shell tells "not found" (127) from
        // "cannot execute" (126) by it.
        throw new WasiError(Errno.NOENT);
    }

    /**
     * Applies a single posix_spawn file action to the target descriptor table.
     * Returns the updated working directory.
     */
    #applyFdOp(fds: FdTable, ptr: number, cwd: string): string {
        const view = this.view;
        const cmd = view.getUint8(ptr);
        const fd = view.getUint32(ptr + 4, true);
        const srcFd = view.getUint32(ptr + 8, true);

        switch (cmd) {
            case FdOp.CLOSE: {
                // Do not close preopened root directories.
                const entry = fds.get(fd);
                if (!(entry.kind === "dir" && entry.preopen)) fds.close(fd);
                return cwd;
            }

            case FdOp.DUP2: {
                const copy = fds.dup(srcFd);
                fds.renumber(copy, fd);
                fds.setCloexec(fd, false);
                return cwd;
            }

            case FdOp.OPEN: {
                const path = this.#string(view.getUint32(ptr + 12, true), view.getUint32(ptr + 16, true));
                const oflags = view.getUint16(ptr + 24, true);
                const rights = view.getBigUint64(ptr + 32, true);
                const fdflags = view.getUint16(ptr + 48, true);
                const opened = this.#open(fds, cwd, path, oflags, rights, fdflags);
                fds.renumber(opened, fd);
                return cwd;
            }

            case FdOp.CHDIR:
                return this.#string(view.getUint32(ptr + 12, true), view.getUint32(ptr + 16, true));

            case FdOp.FCHDIR: {
                const entry = fds.get(fd);
                if (entry.kind !== "dir") throw new WasiError(Errno.NOTDIR);
                return entry.guestPath;
            }

            default:
                throw new WasiError(Errno.INVAL);
        }
    }

    /** Opens a file or directory path into the descriptor table. */
    #open(fds: FdTable, cwd: string, path: string, oflags: number, rights: bigint, fdflags: number): number {
        const absolute = path.startsWith("/") ? path : `${cwd}/${path}`;
        const guestPath = resolveInPreopens(fds.preopens, absolute, outsideOpen(oflags));
        if (inDev(guestPath)) return this.#openDev(fds, guestPath, oflags, rights);
        if ((oflags & Oflags.DIRECTORY) === 0) {
            try {
                const handle = this.#vfs.open(guestPath, openOptions(oflags, rights, fdflags));
                return fds.insert({
                    kind: "file",
                    guestPath,
                    fdflags,
                    file: OpenFile.create(handle, (fdflags & Fdflags.APPEND) !== 0),
                });
            } catch (e) {
                if (!(e instanceof WasiError) || e.errno !== Errno.ISDIR) throw e;
            }
        }
        // Path is a directory or explicitly requested as one.
        if (this.#vfs.stat(guestPath, true).filetype !== Filetype.DIRECTORY) {
            throw new WasiError(Errno.NOTDIR);
        }
        if (oflags & Oflags.EXCL) throw new WasiError(Errno.EXIST);
        if (rights & Rights.FD_WRITE) throw new WasiError(Errno.ISDIR);
        return fds.insert({ kind: "dir", guestPath, preopen: false });
    }

    /** Opens `/dev` or a file in it. Opening paths like `/dev/stdout` duplicates their referenced descriptor. */
    #openDev(fds: FdTable, guestPath: string, oflags: number, rights: bigint): number {
        const node = devNode(guestPath);
        if (oflags & Oflags.EXCL) throw new WasiError(Errno.EXIST);
        if (node === undefined) {
            if (rights & Rights.FD_WRITE) throw new WasiError(Errno.ISDIR);
            return fds.insert({ kind: "dir", guestPath, preopen: false });
        }
        if (oflags & Oflags.DIRECTORY) throw new WasiError(Errno.NOTDIR);
        if ("stdio" in node) return fds.dup(node.stdio);
        return fds.insert({ kind: "device", device: node.device });
    }

    /** Stat a path, in `/dev` or on the filesystem. */
    #stat(guestPath: string, follow: boolean): Stat {
        return inDev(guestPath) ? devStat(guestPath) : this.#vfs.stat(guestPath, follow);
    }

    /** A path to change on the filesystem; nothing in `/dev` can be made, moved or removed. */
    #onFilesystem(guestPath: string): string {
        if (inDev(guestPath)) throw new WasiError(Errno.ACCES);
        return guestPath;
    }

    /** Computes the deadline timestamp on performance.now()'s clock. */
    #deadline(base: number): number {
        const view = this.view;
        const clockId = view.getUint32(base + 16, true);
        const timeout = Number(view.getBigUint64(base + 24, true)) / 1e6;
        const flags = view.getUint16(base + 40, true);
        if ((flags & ABSTIME) === 0) return performance.now() + timeout;
        // Convert absolute timestamp to relative timeout from current time.
        const now = clockId === 0 ? Date.now() : performance.now();
        return performance.now() + Math.max(0, timeout - now);
    }

    /**
     * Whether a descriptor is ready for what a subscription asked about, and
     * with how much. Undefined means not ready — a pipe with no bytes in it
     * and a writer still open, or a full one with a reader still open.
     */
    #readiness(fd: number, type: number): { nbytes: number; error: number; hangup: boolean } | undefined {
        let entry: FdEntry;
        try {
            entry = this.#fds.get(fd);
        } catch {
            return { nbytes: 0, error: Errno.BADF, hangup: false };
        }
        if (entry.kind === "pipe") {
            if (type === Eventtype.FD_WRITE) {
                if (entry.pipe.readers === 0) {
                    return { nbytes: 0, error: Errno.SUCCESS, hangup: true };
                }
                const room = entry.pipe.capacity - entry.pipe.available;
                if (room === 0) return undefined; // full, and someone is reading
                return { nbytes: room, error: Errno.SUCCESS, hangup: false };
            }
            // The writer count is read first: taken the other way round, a
            // writer that writes and exits between the two reads looks like a
            // pipe that is empty and closed, and its last output is lost.
            const writers = entry.pipe.writers;
            const available = entry.pipe.available;
            if (available > 0) return { nbytes: available, error: Errno.SUCCESS, hangup: false };
            if (writers > 0) return undefined; // still open, still empty
            return { nbytes: 0, error: Errno.SUCCESS, hangup: true }; // end of file
        }
        if (entry.kind === "file" && type === Eventtype.FD_READ) {
            const size = this.#vfs.statHandle(entry.file.handle).size;
            return {
                nbytes: Math.max(0, size - entry.file.position),
                error: Errno.SUCCESS,
                hangup: false,
            };
        }
        // Directories, the standard streams, and any file being written: there
        // is nothing that could change the answer later.
        return { nbytes: 0, error: Errno.SUCCESS, hangup: false };
    }

    /**
     * fd_read and fd_pread. `at` is undefined for the positional form, which
     * reads from and advances the file offset.
     */
    #read(fd: number, iovsPtr: number, iovsLen: number, at: number | undefined, readPtr: number): number {
        const entry = this.#fds.get(fd);
        if (entry.kind === "dir") throw new WasiError(Errno.ISDIR);
        let total = 0;
        if (entry.kind === "pipe") {
            if (entry.end !== "read") throw new WasiError(Errno.BADF);
            if (at !== undefined) throw new WasiError(Errno.SPIPE);
            for (const iov of this.iovecs(iovsPtr, iovsLen)) {
                if (iov.length === 0) continue;
                let chunk = entry.pipe.read(iov.length);
                if (chunk.length === 0) {
                    if (total > 0) break;
                    chunk = this.#waitForInput(entry, fd, iov.length);
                    if (chunk.length === 0) break;
                }
                this.writeBytes(iov.byteOffset, chunk);
                total += chunk.length;
                if (chunk.length < iov.length) break;
            }
            this.view.setUint32(readPtr, total, true);
            return Errno.SUCCESS;
        }
        if (entry.kind === "device") {
            // /dev/null is at end of file; /dev/zero fills whatever it is asked for.
            if (entry.device === "zero") {
                for (const iov of this.iovecs(iovsPtr, iovsLen)) {
                    iov.fill(0);
                    total += iov.length;
                }
            }
        }
        if (entry.kind === "file") {
            const file = entry.file;
            let position = at ?? file.position;
            for (const iov of this.iovecs(iovsPtr, iovsLen)) {
                if (iov.length === 0) continue;
                const n = this.#vfs.read(file.handle, iov, position);
                position += n;
                total += n;
                if (n < iov.length) break;
            }
            if (at === undefined) file.position = position;
        }
        this.view.setUint32(readPtr, total, true);
        return Errno.SUCCESS;
    }

    /**
     * Waits for data to be available on a pipe or for all writers to close.
     * Returns an empty array on EOF.
     */
    #waitForInput(entry: Extract<FdEntry, { kind: "pipe" }>, fd: number, max: number): Uint8Array {
        const take = (): Uint8Array | undefined => {
            const writers = entry.pipe.writers;
            const chunk = entry.pipe.read(max);
            if (chunk.length > 0) return chunk;
            return writers === 0 ? EMPTY : undefined;
        };
        const now = take();
        if (now !== undefined) return now;
        if (entry.fdflags & Fdflags.NONBLOCK) throw new WasiError(Errno.AGAIN);
        const what = `reading fd ${fd}, which no process has written to`;
        const stallMs = entry.pipe.fedByHost ? Infinity : undefined;
        return blockUntil(take, what, { waiter: this.#pid, stallMs }) ?? EMPTY;
    }

    /**
     * Deliver a signal now, on the guest's stack: libc's callback runs the
     * handler or the default action, which for most signals exits. While libc
     * blocks signals it registers a callback it does not export, and the signal is
     * dropped. A module that registered none gets the default action here.
     */
    #raise(signal: number): void {
        const name = this.#signalCallback;
        if (name === undefined) {
            if (!IGNORED_SIGNALS.has(signal)) this.#impls.proc_exit2(128 + signal);
            return;
        }
        const callback = this.exports?.[name];
        if (typeof callback === "function") callback(signal);
    }

    /** Writes a chunk into a pipe, blocking if the pipe buffer is full. */
    #writeToPipe(entry: Extract<FdEntry, { kind: "pipe" }>, fd: number, chunk: Uint8Array, already: number): number {
        let offset = 0;
        while (offset < chunk.length) {
            if (entry.pipe.readers === 0) {
                if (offset + already > 0) break;
                // As on POSIX, the writer gets SIGPIPE, and EPIPE if it lives.
                this.#raise(SIGPIPE);
                throw new WasiError(Errno.PIPE);
            }
            const n = entry.pipe.write(chunk.subarray(offset));
            offset += n;
            if (n > 0 || offset === chunk.length) continue;
            if (entry.fdflags & Fdflags.NONBLOCK) {
                if (offset + already > 0) break;
                throw new WasiError(Errno.AGAIN);
            }
            blockUntil(
                () => (entry.pipe.available < entry.pipe.capacity || entry.pipe.readers === 0 ? true : undefined),
                `writing fd ${fd}, whose pipe is full and unread`,
                { waiter: this.#pid },
            );
        }
        return offset;
    }

    /** fd_write and fd_pwrite implementation. */
    #write(fd: number, iovsPtr: number, iovsLen: number, at: number | undefined, writtenPtr: number): number {
        const entry = this.#fds.get(fd);
        if (entry.kind === "dir") throw new WasiError(Errno.ISDIR);
        if (entry.kind === "pipe" && at !== undefined) throw new WasiError(Errno.SPIPE);
        let written = 0;
        let position = entry.kind === "file" ? (at ?? entry.file.position) : 0;
        for (const iov of this.iovecs(iovsPtr, iovsLen)) {
            if (entry.kind === "device") {
                written += iov.length;
                continue;
            }
            if (entry.kind === "stdio") {
                // Copy buffer to ensure sink handlers do not retain shared memory views.
                const chunk = iov.slice();
                (entry.which === 2 ? this.#opts.onStderr : this.#opts.onStdout)(chunk);
                written += chunk.length;
                continue;
            }
            if (entry.kind === "pipe") {
                if (entry.end !== "write") throw new WasiError(Errno.BADF);
                const n = this.#writeToPipe(entry, fd, iov, written);
                written += n;
                if (n < iov.length) break;
                continue;
            }
            const file = entry.file;
            const n = this.#vfs.write(file.handle, iov, file.append ? null : position);
            position = file.append ? this.#vfs.statHandle(file.handle).size : position + n;
            written += n;
        }
        if (entry.kind === "file" && at === undefined) entry.file.position = position;
        this.view.setUint32(writtenPtr, written, true);
        return Errno.SUCCESS;
    }

    #writeFilestat(ptr: number, stats: Stat): void {
        const view = this.view;
        view.setBigUint64(ptr, BigInt(stats.dev), true);
        view.setBigUint64(ptr + 8, BigInt(stats.ino), true);
        view.setUint8(ptr + 16, stats.filetype);
        view.setBigUint64(ptr + 24, BigInt(stats.nlink), true);
        view.setBigUint64(ptr + 32, BigInt(stats.size), true);
        view.setBigUint64(ptr + 40, nanos(stats.atimeMs), true);
        view.setBigUint64(ptr + 48, nanos(stats.mtimeMs), true);
        view.setBigUint64(ptr + 56, nanos(stats.ctimeMs), true);
    }

    /** Writes zeroed filestat structure for pseudo-files (stdio streams and pipes). */
    #writeEmptyFilestat(ptr: number, filetype: number): number {
        const view = this.view;
        for (let i = 0; i < 64; i += 8) view.setBigUint64(ptr + i, 0n, true);
        view.setUint8(ptr + 16, filetype);
        return Errno.SUCCESS;
    }

    #sizes(items: Uint8Array[], countPtr: number, bufSizePtr: number): number {
        const view = this.view;
        view.setUint32(countPtr, items.length, true);
        view.setUint32(
            bufSizePtr,
            items.reduce((n, s) => n + s.length, 0),
            true,
        );
        return Errno.SUCCESS;
    }

    #strings(items: Uint8Array[], pointersPtr: number, bufPtr: number): number {
        const view = this.view;
        let offset = bufPtr;
        for (const [i, item] of items.entries()) {
            view.setUint32(pointersPtr + i * 4, offset, true);
            this.writeBytes(offset, item);
            offset += item.length;
        }
        return Errno.SUCCESS;
    }
}

/** Returns the WASI filetype for a descriptor table entry. */
function filetypeOfEntry(entry: FdEntry): number {
    switch (entry.kind) {
        case "stdio":
        case "device":
            return Filetype.CHARACTER_DEVICE;
        case "dir":
            return Filetype.DIRECTORY;
        case "file":
            return Filetype.REGULAR_FILE;
        case "pipe":
            return Filetype.UNKNOWN;
    }
}

/**
 * What an open of a path outside every preopen answers: ENOTCAPABLE when it
 * would create the file, ENOENT otherwise, since nothing can exist there.
 */
function outsideOpen(oflags: number): Errno {
    return (oflags & Oflags.CREATE) !== 0 ? Errno.NOTCAPABLE : Errno.NOENT;
}

/** Formats process IDs for diagnostic messages. */
function describe(pids: number[]): string {
    return pids.length === 1 ? `pid ${pids[0]}` : `any of pids ${pids.join(", ")}`;
}

/** Parses KEY=value strings into an environment map. */
function parseEnv(entries: string[]): Record<string, string> {
    const env: Record<string, string> = {};
    for (const entry of entries) {
        const eq = entry.indexOf("=");
        if (eq > 0) env[entry.slice(0, eq)] = entry.slice(eq + 1);
    }
    return env;
}

function nanos(ms: number): bigint {
    return BigInt(Math.round(ms * 1e6));
}

/** Returns resolved [atime, mtime] in milliseconds based on flags. */
function times(stats: Stat, atim: bigint, mtim: bigint, fstflags: number): [number, number] {
    const now = Date.now();
    const atime = fstflags & Fstflags.ATIM_NOW ? now : fstflags & Fstflags.ATIM ? Number(atim) / 1e6 : stats.atimeMs;
    const mtime = fstflags & Fstflags.MTIM_NOW ? now : fstflags & Fstflags.MTIM ? Number(mtim) / 1e6 : stats.mtimeMs;
    return [atime, mtime];
}

/**
 * Fills target buffer with cryptographically secure random bytes using WebCrypto.
 * Uses an intermediate buffer because SharedArrayBuffer views are rejected by getRandomValues.
 */
function randomBytes(into: Uint8Array): void {
    // 64 KiB is the maximum chunk size accepted by getRandomValues.
    const chunk = new Uint8Array(Math.min(into.length, 65536));
    for (let at = 0; at < into.length; at += chunk.length) {
        const piece = chunk.subarray(0, Math.min(chunk.length, into.length - at));
        crypto.getRandomValues(piece);
        into.set(piece, at);
    }
}
